import { getCookie } from "@/utilities/cookie";
import { setSessionStorageItem } from "@/utilities/storage";
import {
  createInstance,
  enums,
  ListenerPayload,
  ReactSDKClient,
} from "@optimizely/react-sdk";
import { getForcedCtvVariationKey } from "@/lib/ctv-dmas";

export interface OptimizelyUser {
  id: string;
  attributes: {
    [key: string]: string | number | boolean;
  };
}

export interface OptimizelyTestCookieInfo {
  event: string;
  "optimizely-flagKey": string;
  "optimizely-ruleKey": string | null;
  "optimizely-variationKey": string | null;
  decisionEventDispatched: boolean;
}

export interface OptimizelyClient extends ReactSDKClient {}

export interface OptimizelySessionDecisionEvent {
  sessionId: string;
  userId: string;
  customerId?: number;
  sourceInfoId?: number;
  decisionEventDispatched?: boolean;
  enabled: boolean;
  type: string;
  experimentId?: string;
  ruleKey?: string;
  platform?: string;
  flagKey?: string;
  flagId?: string;
  reasons?: string;
  userContextVariables?: string;
  variables?: string;
  variationId?: string;
  variationKey?: string;
}

type OptimizelyNotificationListener<T extends ListenerPayload> = (
  notificationData: T,
) => void;

export interface OptimizelyDecisionNotification extends ListenerPayload {
  type: string;
  userId: string;
  attributes: {
    [key: string]: string;
    sessionId: string;
  };
  decisionInfo: {
    flagKey: string;
    enabled: boolean;
    experimentId: string;
    variationKey: string;
    ruleKey: string;
    variables?: {
      [key: string]: any;
    };
    variationId?: string;
    reasons?: string[];
    decisionEventDispatched: boolean;
  };
}

export interface MvtStartTestRunRequest {
  sessionId: string;
  experimentName: string;
  selectedVariantName: string;
  experimentId: string;
  selectedVariantId: string;
  flowId?: string;
  deviceId?: string;
  visitorId?: number;
  customerId?: number;
  sourceInfoId?: number;
  platform?: string;
}

let optimizelyClientInstance: ReactSDKClient | null = null;

const logDecisionEvent = async (event: OptimizelySessionDecisionEvent) => {
  // If the decision event is not dispatched,
  // then there is no point in recording the event according to
  // an Optimizely engineer.
  if (!event.decisionEventDispatched) {
    return;
  }

  const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

  const sessionDecisionEventResponse = fetch(
    `${apiUrl}/optimizely/session-decision-event`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(event),
    },
  ).catch((error) =>
    console.error({
      message: `Failed upserting Optimizely decision event into DB. ${(error as Error).message}`,
    }),
  );

  if (
    event.flagKey &&
    event.variationKey &&
    event.experimentId &&
    event.variationId
  ) {
    const experimentName = event.flagKey;
    const selectedVariantName =
      event.variationKey.toLowerCase().trim() === "control"
        ? "CONTROL"
        : event.variationKey;

    const mvtStartTestRun: MvtStartTestRunRequest = {
      experimentName,
      experimentId: experimentName,
      selectedVariantId: selectedVariantName,
      selectedVariantName,
      sessionId: event.sessionId,
      ...(event.customerId && {
        customerId: event.customerId,
      }),
      ...(event.sourceInfoId && {
        sourceInfoId: event.sourceInfoId,
      }),
      platform: event.platform ?? "amerisave-nova",
    };

    const userContextVariables = JSON.parse(event.userContextVariables ?? "{}");

    if (!event.enabled || userContextVariables.isBot) {
      return;
    }

    const startTestResponse = fetch(`${apiUrl}/optimizely/start-test`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(mvtStartTestRun),
    }).catch((error) =>
      console.error({
        message: `Failed starting MVT test run. ${(error as Error).message}`,
      }),
    );

    await Promise.all([sessionDecisionEventResponse, startTestResponse]);
    setSessionStorageItem("optimizelyTestRunning", "true");
    window?.dispatchEvent(new Event("storage"));
  }
};

const handleDecisionNotification: OptimizelyNotificationListener<
  OptimizelyDecisionNotification
> = (event) => {
  const sessionId = event.attributes?.sessionId;

  if (
    typeof window === "undefined" ||
    event.type !== enums.DECISION_NOTIFICATION_TYPES.FLAG ||
    typeof sessionId !== "string" ||
    !sessionId
  ) {
    return;
  }

  const optimizelyClient = getOptimizelyClient();
  const config = optimizelyClient.getOptimizelyConfig();

  if (!config) {
    return;
  }

  let userContextVariables: string | undefined;

  try {
    if (event.attributes && Object.keys(event.attributes).length) {
      userContextVariables = JSON.stringify(event.attributes);
    }
  } catch {}

  let flagKey: string | undefined = event.decisionInfo.flagKey,
    flagId: string | undefined,
    experimentId: string | undefined =
      event.decisionInfo.experimentId || undefined,
    // Closest to an experiment name.
    experimentKey: string | undefined = event.decisionInfo.ruleKey || undefined,
    variationId: string | undefined =
      event.decisionInfo.variationId || undefined,
    // Closest to a variation name.
    variationKey: string | undefined =
      event.decisionInfo.variationKey || undefined,
    variationVariables: string | undefined,
    reasons: string | undefined;

  const forcedVariationKey = getForcedCtvVariationKey(
    event.decisionInfo.flagKey,
    event.attributes,
    event.decisionInfo.enabled,
  );

  if (forcedVariationKey) {
    variationKey = forcedVariationKey;
  }

  try {
    if (event.decisionInfo?.variables?.length) {
      variationVariables = JSON.stringify(event.decisionInfo.variables);
    }
  } catch {}

  try {
    if (event.decisionInfo?.reasons?.length) {
      reasons = JSON.stringify(event.decisionInfo.reasons);
    }
  } catch {}

  const flag = config.featuresMap[event.decisionInfo.flagKey];

  if (flag) {
    flagKey = flag.key;
    flagId = flag.id;

    for (const experiment of flag.experimentRules) {
      if (event.decisionInfo.ruleKey === experiment.key) {
        experimentId = experiment.id;
        experimentKey = experiment.key;

        for (const variation of Object.values(experiment.variationsMap)) {
          if (variationKey === variation.key) {
            variationId = variation.id;
            variationKey = variation.key;

            if (Object.keys(variation.variablesMap).length) {
              try {
                variationVariables = JSON.stringify(
                  Object.values(variation.variablesMap).map((variable) => {
                    const modifiedVariable = { ...variable };

                    if (modifiedVariable.type === "json") {
                      try {
                        modifiedVariable.value = JSON.parse(variable.value);
                      } catch {}
                    }

                    return modifiedVariable;
                  }),
                );
              } catch {}
            }
          }
        }
      }
    }
  }

  logDecisionEvent({
    sessionId,
    userId: event.userId,
    type: event.type,
    enabled: event.decisionInfo.enabled,
    platform: "amerisave-nova",
    ...(userContextVariables && { userContextVariables }),
    ...(flagKey && { flagKey }),
    ...(flagId && { flagId }),
    ...(experimentId && { experimentId }),
    ...(experimentKey && { ruleKey: experimentKey }),
    ...(variationId && { variationId }),
    ...(variationKey && { variationKey }),
    ...(variationVariables && { variables: variationVariables }),
    ...(typeof event.decisionInfo?.decisionEventDispatched === "boolean" && {
      decisionEventDispatched: event.decisionInfo.decisionEventDispatched,
    }),
    ...(reasons && { reasons }),
  });
};

const createOptimizelyClient = (): ReactSDKClient => {
  const client = createInstance({
    sdkKey: process.env.NEXT_PUBLIC_OPTIMIZELY_SDK_KEY ?? "",
  });

  client.notificationCenter.addNotificationListener(
    enums.NOTIFICATION_TYPES.DECISION,
    handleDecisionNotification,
  );

  return client;
};

export const getOptimizelyClient = (): ReactSDKClient => {
  if (optimizelyClientInstance === null) {
    optimizelyClientInstance = createOptimizelyClient();
  }
  return optimizelyClientInstance;
};

export interface UpdateTestRunRequest {
  sessionId: string;
  deviceId?: string;
  visitorId?: number;
  customerId?: number;
  sourceInfoId?: string;
  platform?: string;
}

export const updateOptimizelyInfo = async (
  params: UpdateTestRunRequest,
): Promise<void> => {
  const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

  fetch(`${apiUrl}/optimizely/update-test`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  }).catch((error) =>
    console.error({
      message: `Failed updating MVT test run. ${(error as Error).message}`,
    }),
  );
};

export const getPreviousOptimizelyTestInfo = (): Record<
  string,
  OptimizelyTestCookieInfo
> => {
  return (
    (getCookie("optimizelyFeatureTests") as {
      [key: string]: OptimizelyTestCookieInfo;
    }) ||
    (getCookie("applyOptimizelyFeatureTests") as {
      [key: string]: OptimizelyTestCookieInfo;
    }) ||
    {}
  );
};
